import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Field from './SiteField';

// The Contact page's enquiry form (issue #287): first and last name, email, Topic and
// message, plus an optional phone and business name. POST /api/site/enquiry
// (websiteEnquiryService.js) logs it on the business the email belongs to,
// or leaves it in CRM's Website Enquiries list; the visitor sees the same
// thank-you either way. Spam handling is the Trade Enquiry's: a hidden field
// and the time the form was open.

const EMPTY = { first_name: '', last_name: '', email: '', phone: '', business_name: '', topic_id: '', message: '', website: '' };

// Where a Topic's Redirect to sends the visitor (backend
// models/enquiryTopic.js REDIRECTS).
const REDIRECTS = {
  trade_enquiry: {
    path: '/trade-enquiry',
    button: 'Become a stockist',
    text: 'For a trade account, tell us about your business on our Become a stockist form. It only takes a minute.',
  },
};

let topicsRequest = null;
function loadTopics() {
  if (!topicsRequest) {
    topicsRequest = axios.get('/api/site/enquiry-topics')
      .then((res) => res.data.data || [])
      .catch(() => { topicsRequest = null; return []; });
  }
  return topicsRequest;
}

function ContactEnquiryForm({ copy, go }) {
  const [form, setForm] = useState(EMPTY);
  const [topics, setTopics] = useState([]);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const openedAt = useRef(Date.now());

  useEffect(() => {
    let cancelled = false;
    loadTopics().then((data) => { if (!cancelled) setTopics(data); });
    return () => { cancelled = true; };
  }, []);

  const set = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));
  // A Topic with a Redirect to (Trade account, to begin with) sends the
  // visitor to that page instead of taking the enquiry here.
  const redirect = REDIRECTS[topics.find((t) => String(t.id) === String(form.topic_id))?.redirect_to];

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    setSending(true);
    try {
      await axios.post('/api/site/enquiry', { ...form, form_opened_at: openedAt.current });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.message || "Sorry, we couldn't send your enquiry. Please try again, or email us.");
    }
    setSending(false);
  };

  if (sent) {
    return (
      <div className="ks-enquiry-done" role="status">
        <h2 className="ks-h2">{copy.thanksTitle}</h2>
        <p className="ks-lead" style={{ margin: 0 }}>{copy.thanksBody.replace('{email}', form.email)}</p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="ks-h2">{copy.formTitle}</h2>
      <form className="ks-form" style={{ marginTop: 24 }} onSubmit={submit}>
        {/* Hidden from people; a bot that fills it in is quietly ignored. */}
        <div className="ks-form-trap" aria-hidden="true">
          <label htmlFor="ce-website">Website</label>
          <input id="ce-website" name="website" type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set('website')} />
        </div>

        <div className="ks-form-row">
          <Field label="First name" htmlFor="ce-first-name">
            <input id="ce-first-name" value={form.first_name} onChange={set('first_name')} maxLength={75} required autoComplete="given-name" />
          </Field>
          <Field label="Last name" htmlFor="ce-last-name">
            <input id="ce-last-name" value={form.last_name} onChange={set('last_name')} maxLength={75} required autoComplete="family-name" />
          </Field>
        </div>
        <div className="ks-form-row">
          <Field label="Email" htmlFor="ce-email">
            <input id="ce-email" type="email" value={form.email} onChange={set('email')} maxLength={255} required autoComplete="email" />
          </Field>
          <Field label="Phone (optional)" htmlFor="ce-phone">
            <input id="ce-phone" type="tel" value={form.phone} onChange={set('phone')} maxLength={30} autoComplete="tel" />
          </Field>
        </div>
        <Field label="Business name (optional)" htmlFor="ce-business">
          <input id="ce-business" value={form.business_name} onChange={set('business_name')} maxLength={150} autoComplete="organization" />
        </Field>
        <Field label="What's it about?" htmlFor="ce-topic">
          <select id="ce-topic" value={form.topic_id} onChange={set('topic_id')} required>
            <option value="">Choose…</option>
            {topics.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </Field>
        {redirect ? (
          <div className="ks-enquiry-redirect">
            <p>{redirect.text}</p>
            <div className="ks-btn-row">
              <a href={redirect.path} onClick={go ? go(redirect.path) : undefined} className="ks-btn ks-btn-solid">{redirect.button}</a>
            </div>
          </div>
        ) : (
          <>
            <Field label="Message" htmlFor="ce-message">
              <textarea id="ce-message" value={form.message} onChange={set('message')} maxLength={2000} rows={6} required />
            </Field>

            {error && <p className="ks-form-error" role="alert">{error}</p>}
            <div className="ks-btn-row" style={{ marginTop: 8 }}>
              <button type="submit" className="ks-btn ks-btn-solid" disabled={sending}>
                {sending ? 'Sending…' : 'Send enquiry'}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}

export default ContactEnquiryForm;
